import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useInterval } from '../hooks';
export const ClockTickContext = React.createContext(null);
export function useClockTick() {
    const context = React.useContext(ClockTickContext);
    if (context == null) {
        throw new Error('useClockTick must be used within a ClockTickContext');
    }
    return context;
}
export function ClockTickProvider({ children, ...props }) {
    if (useContext(ClockTickContext) != null) {
        throw new Error('ClockTickProvider can only be used once');
    }
    const { timing, clock, clockState } = props;
    const pollClock = useCallback(() => timing.lastPosition(clock.now), [clock, timing]);
    const [position, setPosition] = useState(pollClock);
    const positionRef = useRef(position);
    const updateState = useCallback(() => {
        const position = pollClock();
        if (position.absTick != positionRef.current.absTick) {
            positionRef.current = position;
            setPosition(positionRef.current);
        }
    }, [pollClock]);
    useEffect(() => {
        if (clock != null) {
            const handleSeek = () => updateState();
            const handlePause = () => updateState();
            const handleResume = () => updateState();
            const handleEnd = () => updateState();
            const handleReset = () => updateState();
            clock.addEventListener('resumed', handleResume);
            clock.addEventListener('ended', handleEnd);
            clock.addEventListener('seeked', handleSeek);
            clock.addEventListener('paused', handlePause);
            clock.addEventListener('reset', handleReset);
            return () => {
                clock.removeEventListener('seeked', handleSeek);
                clock.removeEventListener('resumed', handleResume);
                clock.removeEventListener('paused', handlePause);
                clock.removeEventListener('ended', handleEnd);
                clock.removeEventListener('reset', handleReset);
            };
        }
    }, [clock, updateState]);
    useEffect(() => {
        updateState();
    }, [clock, updateState]);
    useInterval(() => {
        if (clockState.isRunning) {
            updateState();
        }
    }, 25);
    return React.createElement(ClockTickContext.Provider, { value: position }, children);
}
//# sourceMappingURL=ClockTickContext.js.map